import { CubeQuery, useInsightsQuery } from "model/useInsightsQueries";

// ─── Data types ──────────────────────────────────────────────

export type HighLevelMetricsData = {
  "HighLevelMetrics.sumFarmers": number;
  "HighLevelMetrics.sumAcres": number;
  "HighLevelMetrics.sumActions": number;
  "HighLevelMetrics.sumFarmActionPlans": number;
  "HighLevelMetrics.sumSoilAssessments": number;
  "HighLevelMetrics.sumGrazingDays": number;
  "HighLevelMetrics.sumRecoveryTime": number;
  "HighLevelMetrics.sumPcf": number;
  "HighLevelMetrics.sumBiodiversityAssessments": number;
};

export type CarcaseSummaryAvgData = {
  "CarcaseSummary.avgAgeAtSlaughter": number;
  "CarcaseSummary.avgCarcaseColdWeight": number;
  "CarcaseSummary.avgDailyLifeweightGain": number;
  "CarcaseSummary.avgCarcaseWithinDesiredGrade": number;
};

export type CarcaseSummaryFarmerData = {
  "CarcaseSummary.avgAgeAtSlaughterFarmer": number;
  "CarcaseSummary.avgCarcaseColdWeightFarmer": number;
  "CarcaseSummary.estLifetimeDlwgFarmer": number;
  "CarcaseSummary.carcaseWithinDesiredGradeFarmer": number;
  "CarcaseSummary.avgAgeAtSlaughterBenchmark": number;
  "CarcaseSummary.avgCarcaseColdWeightBenchmark": number;
  "CarcaseSummary.estLifetimeDlwgBenchmark": number;
  "CarcaseSummary.carcaseWithinDesiredGradeBenchmark": number;
  "CarcaseSummary.periodStart": string;
  "CarcaseSummary.periodEnd": string;
};

export type BiodiversityScalarData = {
  "Biodiversity.distinctHabitatCodeCount": number;
  "Biodiversity.totalLengthM": number;
  "Biodiversity.totalAreaHa": number;
  "Biodiversity.totalAreaAcres": number;
};

export type HabitatPieData = {
  "Biodiversity.habitatGroup": string;
  "Biodiversity.totalAreaHa": number;
};

export type HabitatByNameData = {
  "Biodiversity.habitatName": string;
  "Biodiversity.habitatGroup": string;
  "Biodiversity.totalAreaHa": number;
  "Biodiversity.totalLengthM": number;
};

export type BirdScalarData = {
  "BirdObservations.distinctBirdCount": number;
};

export type BirdPieData = {
  "BirdObservations.bird": string;
  "BirdObservations.distinctBirdCount": number;
};

export type EmissionsPcfData = {
  "Emissions.avgValue": number;
};

export type EmissionsBySourceData = {
  "Emissions.parameter": string;
  "Emissions.avgValue": number;
};

export type EmissionsYearData = {
  "Emissions.year": number;
};

export type SfiAdviceData = {
  "SfiAdvice.sfiCode": string;
  "SfiAdvice.title": string;
  "SfiAdvice.description": string;
  "SfiAdvice.income": number;
};

export type NutrientManagementData = {
  "NutrientManagement.description": string;
  "NutrientManagement.strengthOrWeakness": string;
};

export type ActionsLegacyData = {
  "ActionsLegacy.action": string;
  "ActionsLegacy.category": string;
  "ActionsLegacy.image": string;
};

export type GrazingEventsByFieldData = {
  "GrazingEvents.fieldName": string;
  "GrazingEvents.count": number;
  "GrazingEvents.sumKgDmUsed": number;
  "GrazingEvents.avgFieldOnTime": number;
  "GrazingEvents.avgDryMatterOutPerHectar": number;
};

export type YieldSummaryData = {
  "YieldSummary.yieldTypeDisplay": string;
  "YieldSummary.totalAmount": number;
  "YieldSummary.amountUnitDisplay": string;
};

export type YieldByTypeYearData = {
  "YieldSummary.yieldTypeDisplay": string;
  "YieldSummary.amountUnitDisplay": string;
  "YieldSummary.totalAmount": number;
  "YieldSummary.eventDate.year": string;
};

export type FeedInStoreData = {
  "FeedInStore.feedTypeDisplay": string;
  "FeedInStore.feedInStock": number;
  "FeedInStore.amountUnitDisplay": string;
};

export type FeedConsumptionData = {
  "FeedInStore.feedTypeDisplay": string;
  "FeedInStore.feedInStock": number;
};

// ─── Queries ─────────────────────────────────────────────────

const highLevelMetricsQuery: CubeQuery = {
  measures: [
    "HighLevelMetrics.sumFarmers",
    "HighLevelMetrics.sumAcres",
    "HighLevelMetrics.sumActions",
    "HighLevelMetrics.sumFarmActionPlans",
    "HighLevelMetrics.sumSoilAssessments",
    "HighLevelMetrics.sumGrazingDays",
    "HighLevelMetrics.sumRecoveryTime",
    "HighLevelMetrics.sumPcf",
    "HighLevelMetrics.sumBiodiversityAssessments",
  ],
};

function withYearFilter(
  query: CubeQuery,
  dimension: string,
  year?: number
): CubeQuery {
  if (!year) return query;
  return {
    ...query,
    timeDimensions: [
      ...(query.timeDimensions ?? []),
      {
        dimension,
        dateRange: String(year),
      },
    ],
  };
}

function withEmissionsYearFilter(query: CubeQuery, year?: number): CubeQuery {
  if (!year) return query;
  return {
    ...query,
    filters: [
      ...(query.filters ?? []),
      { member: "Emissions.year", operator: "equals", values: [String(year)] },
    ],
  };
}

function withCarcaseYearFilter(query: CubeQuery, year?: number): CubeQuery {
  if (!year) return query;
  return {
    ...query,
    filters: [
      ...(query.filters ?? []),
      {
        member: "CarcaseSummary.year",
        operator: "equals",
        values: [String(year)],
      },
    ],
  };
}

const carcaseAveragesQuery: CubeQuery = {
  measures: [
    "CarcaseSummary.avgAgeAtSlaughter",
    "CarcaseSummary.avgCarcaseColdWeight",
    "CarcaseSummary.avgDailyLifeweightGain",
    "CarcaseSummary.avgCarcaseWithinDesiredGrade",
  ],
};

const carcaseGaugesQuery: CubeQuery = {
  dimensions: [
    "CarcaseSummary.avgAgeAtSlaughterFarmer",
    "CarcaseSummary.avgCarcaseColdWeightFarmer",
    "CarcaseSummary.estLifetimeDlwgFarmer",
    "CarcaseSummary.carcaseWithinDesiredGradeFarmer",
    "CarcaseSummary.avgAgeAtSlaughterBenchmark",
    "CarcaseSummary.avgCarcaseColdWeightBenchmark",
    "CarcaseSummary.estLifetimeDlwgBenchmark",
    "CarcaseSummary.carcaseWithinDesiredGradeBenchmark",
    "CarcaseSummary.periodStart",
    "CarcaseSummary.periodEnd",
  ],
  order: {
    "CarcaseSummary.periodStart": "desc",
  },
  limit: 1,
};

const biodiversityScalarsQuery: CubeQuery = {
  measures: [
    "Biodiversity.distinctHabitatCodeCount",
    "Biodiversity.totalLengthM",
    "Biodiversity.totalAreaHa",
    "Biodiversity.totalAreaAcres",
  ],
};

const habitatPieQuery: CubeQuery = {
  measures: ["Biodiversity.totalAreaHa"],
  dimensions: ["Biodiversity.habitatGroup"],
  order: { "Biodiversity.totalAreaHa": "desc" },
};

const habitatByNameQuery: CubeQuery = {
  measures: ["Biodiversity.totalAreaHa", "Biodiversity.totalLengthM"],
  dimensions: ["Biodiversity.habitatName", "Biodiversity.habitatGroup"],
  order: { "Biodiversity.totalAreaHa": "desc" },
};

const birdScalarQuery: CubeQuery = {
  measures: ["BirdObservations.distinctBirdCount"],
};

const birdPieQuery: CubeQuery = {
  measures: ["BirdObservations.distinctBirdCount"],
  dimensions: ["BirdObservations.bird"],
  order: { "BirdObservations.distinctBirdCount": "desc" },
};

const emissionsPcfQuery: CubeQuery = {
  measures: ["Emissions.avgValue"],
  filters: [
    { member: "Emissions.parameter", operator: "equals", values: ["pcf"] },
  ],
};

const emissionsBySourceQuery: CubeQuery = {
  measures: ["Emissions.avgValue"],
  dimensions: ["Emissions.parameter"],
  filters: [
    {
      member: "Emissions.parameter",
      operator: "notEquals",
      values: ["pcf"],
    },
  ],
  order: { "Emissions.avgValue": "desc" },
};

const sfiAdviceQuery: CubeQuery = {
  dimensions: [
    "SfiAdvice.sfiCode",
    "SfiAdvice.title",
    "SfiAdvice.description",
    "SfiAdvice.income",
  ],
};

const nutrientStrengthsQuery: CubeQuery = {
  dimensions: ["NutrientManagement.description"],
  filters: [
    {
      member: "NutrientManagement.strengthOrWeakness",
      operator: "equals",
      values: ["strength"],
    },
  ],
};

const nutrientWeaknessesQuery: CubeQuery = {
  dimensions: ["NutrientManagement.description"],
  filters: [
    {
      member: "NutrientManagement.strengthOrWeakness",
      operator: "equals",
      values: ["weakness"],
    },
  ],
};

const recommendationsQuery: CubeQuery = {
  dimensions: ["ActionsLegacy.action"],
  filters: [
    {
      member: "ActionsLegacy.category",
      operator: "equals",
      values: ["Improve Water and Soil Quality"],
    },
  ],
};

const grazingEventsByFieldQuery: CubeQuery = {
  measures: [
    "GrazingEvents.count",
    "GrazingEvents.sumKgDmUsed",
    "GrazingEvents.avgFieldOnTime",
    "GrazingEvents.avgDryMatterOutPerHectar",
  ],
  dimensions: ["GrazingEvents.fieldName"],
  filters: [
    {
      member: "GrazingEvents.grazingSeason",
      operator: "equals",
      values: [String(new Date().getFullYear())],
    },
  ],
  order: { "GrazingEvents.sumKgDmUsed": "desc" },
};

// ─── Helpers ──────────────────────────────────────────────────

function withFarmerFilter(
  query: CubeQuery,
  member: string,
  farmerGuid?: string
): CubeQuery {
  if (!farmerGuid) return query;
  return {
    ...query,
    filters: [
      ...(query.filters ?? []),
      { member, operator: "equals", values: [farmerGuid] },
    ],
  };
}

function farmerKey(base: string, farmerGuid?: string): string {
  return farmerGuid ? `${base}_${farmerGuid}` : base;
}

// ─── Hooks ───────────────────────────────────────────────────

export function useHighLevelMetrics() {
  return useInsightsQuery<HighLevelMetricsData>(
    highLevelMetricsQuery,
    "highLevelMetrics"
  );
}

export function useCarcaseAverages(year?: number) {
  return useInsightsQuery<CarcaseSummaryAvgData>(
    withCarcaseYearFilter(carcaseAveragesQuery, year),
    `carcaseAverages_${year ?? "all"}`
  );
}

export function useCarcaseGauges(farmerGuid?: string, year?: number) {
  return useInsightsQuery<CarcaseSummaryFarmerData>(
    withCarcaseYearFilter(
      withFarmerFilter(
        carcaseGaugesQuery,
        "CarcaseSummary.userGuid",
        farmerGuid
      ),
      year
    ),
    farmerKey(`carcaseGauges_${year ?? "all"}`, farmerGuid)
  );
}

export type CarcaseYearData = {
  "CarcaseSummary.year": number;
};

export function useCarcaseAvailableYears(farmerGuid?: string) {
  return useInsightsQuery<CarcaseYearData>(
    withFarmerFilter(
      {
        dimensions: ["CarcaseSummary.year"],
        order: { "CarcaseSummary.year": "desc" },
      },
      "CarcaseSummary.userGuid",
      farmerGuid
    ),
    farmerKey("carcaseYears", farmerGuid)
  );
}

export function useBiodiversityScalars(farmerGuid?: string) {
  return useInsightsQuery<BiodiversityScalarData>(
    withFarmerFilter(
      biodiversityScalarsQuery,
      "Biodiversity.userGuid",
      farmerGuid
    ),
    farmerKey("biodiversityScalars", farmerGuid)
  );
}

export function useHabitatPieData(farmerGuid?: string) {
  return useInsightsQuery<HabitatPieData>(
    withFarmerFilter(habitatPieQuery, "Biodiversity.userGuid", farmerGuid),
    farmerKey("habitatPie", farmerGuid)
  );
}

export function useHabitatByName(farmerGuid?: string) {
  return useInsightsQuery<HabitatByNameData>(
    withFarmerFilter(habitatByNameQuery, "Biodiversity.userGuid", farmerGuid),
    farmerKey("habitatByName", farmerGuid)
  );
}

export function useBirdScalar(farmerGuid?: string) {
  return useInsightsQuery<BirdScalarData>(
    withFarmerFilter(birdScalarQuery, "BirdObservations.userGuid", farmerGuid),
    farmerKey("birdScalar", farmerGuid)
  );
}

export function useBirdPieData(farmerGuid?: string) {
  return useInsightsQuery<BirdPieData>(
    withFarmerFilter(birdPieQuery, "BirdObservations.userGuid", farmerGuid),
    farmerKey("birdPie", farmerGuid)
  );
}

export function useEmissionsAvailableYears(farmerGuid?: string) {
  return useInsightsQuery<EmissionsYearData>(
    withFarmerFilter(
      {
        dimensions: ["Emissions.year"],
        order: { "Emissions.year": "desc" },
      },
      "Emissions.userGuid",
      farmerGuid
    ),
    farmerKey("emissionsYears", farmerGuid)
  );
}

export function useEmissionsPcf(farmerGuid?: string, year?: number) {
  return useInsightsQuery<EmissionsPcfData>(
    withEmissionsYearFilter(
      withFarmerFilter(emissionsPcfQuery, "Emissions.userGuid", farmerGuid),
      year
    ),
    farmerKey(`emissionsPcf_${year ?? "all"}`, farmerGuid)
  );
}

export function useEmissionsBySource(farmerGuid?: string, year?: number) {
  return useInsightsQuery<EmissionsBySourceData>(
    withEmissionsYearFilter(
      withFarmerFilter(
        emissionsBySourceQuery,
        "Emissions.userGuid",
        farmerGuid
      ),
      year
    ),
    farmerKey(`emissionsBySource_${year ?? "all"}`, farmerGuid)
  );
}

export function useSfiAdvice(farmerGuid?: string) {
  return useInsightsQuery<SfiAdviceData>(
    withFarmerFilter(sfiAdviceQuery, "SfiAdvice.userGuid", farmerGuid),
    farmerKey("sfiAdvice", farmerGuid)
  );
}

export function useNutrientStrengths(farmerGuid?: string) {
  return useInsightsQuery<NutrientManagementData>(
    withFarmerFilter(
      nutrientStrengthsQuery,
      "NutrientManagement.userGuid",
      farmerGuid
    ),
    farmerKey("nutrientStrengths", farmerGuid)
  );
}

export function useNutrientWeaknesses(farmerGuid?: string) {
  return useInsightsQuery<NutrientManagementData>(
    withFarmerFilter(
      nutrientWeaknessesQuery,
      "NutrientManagement.userGuid",
      farmerGuid
    ),
    farmerKey("nutrientWeaknesses", farmerGuid)
  );
}

export function useRecommendations(farmerGuid?: string) {
  return useInsightsQuery<ActionsLegacyData>(
    withFarmerFilter(
      recommendationsQuery,
      "ActionsLegacy.userGuid",
      farmerGuid
    ),
    farmerKey("recommendations", farmerGuid)
  );
}

export function useGrazingEventsByField(farmerGuid?: string) {
  return useInsightsQuery<GrazingEventsByFieldData>(
    withFarmerFilter(
      grazingEventsByFieldQuery,
      "GrazingEvents.userGuid",
      farmerGuid
    ),
    farmerKey("grazingEventsByField", farmerGuid)
  );
}

export function useYieldByTypePerYear(farmGuid: string | undefined) {
  return useInsightsQuery<YieldByTypeYearData>(
    {
      dimensions: [
        "YieldSummary.yieldTypeDisplay",
        "YieldSummary.amountUnitDisplay",
      ],
      measures: ["YieldSummary.totalAmount"],
      timeDimensions: [
        {
          dimension: "YieldSummary.eventDate",
          granularity: "year",
        },
      ],
      filters: [
        {
          member: "YieldSummary.farmGuid",
          operator: "equals",
          values: [farmGuid ?? ""],
        },
      ],
      order: { "YieldSummary.totalAmount": "desc" },
    },
    `yieldByTypeYear_${farmGuid}`,
    !!farmGuid
  );
}

export function useYieldSummary(farmGuid: string | undefined) {
  return useInsightsQuery<YieldSummaryData>(
    {
      dimensions: [
        "YieldSummary.yieldTypeDisplay",
        "YieldSummary.amountUnitDisplay",
      ],
      measures: ["YieldSummary.totalAmount"],
      filters: [
        {
          member: "YieldSummary.farmGuid",
          operator: "equals",
          values: [farmGuid ?? ""],
        },
      ],
      order: { "YieldSummary.totalAmount": "desc" },
    },
    `yieldSummary_${farmGuid}`,
    !!farmGuid
  );
}

export function useFeedConsumption(farmGuid: string | undefined) {
  return useInsightsQuery<FeedConsumptionData>(
    {
      dimensions: ["FeedInStore.feedTypeDisplay"],
      measures: ["FeedInStore.feedInStock"],
      filters: [
        {
          member: "FeedInStore.farmGuid",
          operator: "equals",
          values: [farmGuid ?? ""],
        },
        {
          member: "FeedInStore.source",
          operator: "equals",
          values: ["consumed"],
        },
      ],
      timeDimensions: [
        {
          dimension: "FeedInStore.eventDate",
          dateRange: "Last 90 days",
        },
      ],
    },
    `feedConsumption90d_${farmGuid}`,
    !!farmGuid
  );
}

export function useFeedInStore(farmGuid: string | undefined) {
  return useInsightsQuery<FeedInStoreData>(
    {
      dimensions: [
        "FeedInStore.feedTypeDisplay",
        "FeedInStore.amountUnitDisplay",
      ],
      measures: ["FeedInStore.feedInStock"],
      filters: [
        {
          member: "FeedInStore.farmGuid",
          operator: "equals",
          values: [farmGuid ?? ""],
        },
      ],
      order: { "FeedInStore.feedInStock": "desc" },
    },
    `feedInStore_${farmGuid}`,
    !!farmGuid
  );
}

// ─── Per-farmer aggregate hooks (for corporate farmer table) ────

export type PerFarmerActionsData = {
  "SubmittedActions.userGuid": string;
  "SubmittedActions.count": number;
  "SubmittedActions.uniqueActionCount": number;
  "SubmittedActions.uniqueActionsOnTrack": number;
  "SubmittedActions.uniqueActionsCompleted": number;
};

export type PerFarmerPcfData = {
  "Emissions.userGuid": string;
  "Emissions.year": number;
  "Emissions.avgValue": number;
};

export type PerFarmerAssessmentData = {
  "AssessmentSubmitted.userGuid": string;
  "AssessmentSubmitted.count": number;
};

export function usePerFarmerActions(year?: number) {
  return useInsightsQuery<PerFarmerActionsData>(
    {
      measures: [
        "SubmittedActions.count",
        "SubmittedActions.uniqueActionCount",
        "SubmittedActions.uniqueActionsOnTrack",
        "SubmittedActions.uniqueActionsCompleted",
      ],
      dimensions: ["SubmittedActions.userGuid"],
      timeDimensions: year
        ? [
            {
              dimension: "SubmittedActions.submissionDate",
              dateRange: String(year),
            },
          ]
        : undefined,
    },
    `perFarmerActions_v2_${year ?? "all"}`
  );
}

export type ActionsByCategoryData = {
  "SubmittedActions.categoryId": string;
  "SubmittedActions.category": string;
  "SubmittedActions.uniqueActionCount": number;
  "SubmittedActions.uniqueActionsOnTrack": number;
  "SubmittedActions.uniqueActionsCompleted": number;
};

export function useActionsByCategory() {
  return useInsightsQuery<ActionsByCategoryData>(
    {
      measures: [
        "SubmittedActions.uniqueActionCount",
        "SubmittedActions.uniqueActionsOnTrack",
        "SubmittedActions.uniqueActionsCompleted",
      ],
      dimensions: ["SubmittedActions.categoryId", "SubmittedActions.category"],
      order: { "SubmittedActions.uniqueActionCount": "desc" },
    },
    "actionsByCategory"
  );
}

// Groups by both farmer and category so that each farmer's actions are counted
// separately. Summing across this result gives totals consistent with the
// per-farmer counts in the farmer table (not deduplicated across farmers).
export type ActionsByFarmerCategoryData = {
  "SubmittedActions.userGuid": string;
  "SubmittedActions.categoryId": string;
  "SubmittedActions.category": string;
  "SubmittedActions.uniqueActionCount": number;
  "SubmittedActions.uniqueActionsOnTrack": number;
  "SubmittedActions.uniqueActionsCompleted": number;
};

export function useActionsByFarmerAndCategory() {
  return useInsightsQuery<ActionsByFarmerCategoryData>(
    {
      measures: [
        "SubmittedActions.uniqueActionCount",
        "SubmittedActions.uniqueActionsOnTrack",
        "SubmittedActions.uniqueActionsCompleted",
      ],
      dimensions: [
        "SubmittedActions.userGuid",
        "SubmittedActions.categoryId",
        "SubmittedActions.category",
      ],
    },
    "actionsByFarmerAndCategory"
  );
}

export type PcfByYearData = {
  "Emissions.year": number;
  "Emissions.avgValue": number;
};

export function usePcfByYear() {
  return useInsightsQuery<PcfByYearData>(
    {
      measures: ["Emissions.avgValue"],
      dimensions: ["Emissions.year"],
      filters: [
        {
          member: "Emissions.parameter",
          operator: "equals",
          values: ["pcf"],
        },
      ],
      order: { "Emissions.year": "asc" },
    },
    "pcfByYear"
  );
}

export function usePerFarmerAssessments() {
  return useInsightsQuery<PerFarmerAssessmentData>(
    {
      measures: ["AssessmentSubmitted.count"],
      dimensions: ["AssessmentSubmitted.userGuid"],
    },
    "perFarmerAssessments"
  );
}

export function usePerFarmerPcf() {
  return useInsightsQuery<PerFarmerPcfData>(
    {
      measures: ["Emissions.avgValue"],
      dimensions: ["Emissions.userGuid", "Emissions.year"],
      filters: [
        {
          member: "Emissions.parameter",
          operator: "equals",
          values: ["pcf"],
        },
      ],
    },
    "perFarmerPcfByYear"
  );
}

// ─── Per-farmer action list (for farmer detail dialog) ────

// ─── Programme farm locations (approximate centroids, for the map) ──

export type FarmLocationData = {
  "ProgrammeFarmLocations.guid": string;
  "ProgrammeFarmLocations.name": string;
  "ProgrammeFarmLocations.centroidLat": number;
  "ProgrammeFarmLocations.centroidLng": number;
};

export function useProgrammeFarmLocations(programmeGuid?: string) {
  return useInsightsQuery<FarmLocationData>(
    {
      dimensions: [
        "ProgrammeFarmLocations.guid",
        "ProgrammeFarmLocations.name",
        "ProgrammeFarmLocations.centroidLat",
        "ProgrammeFarmLocations.centroidLng",
      ],
      filters: programmeGuid
        ? [
            {
              member: "ProgrammeFarmLocations.programmeGuid",
              operator: "equals",
              values: [programmeGuid],
            },
          ]
        : [],
    },
    `programmeFarmLocations_perFarmer_${programmeGuid ?? "all"}`,
    !!programmeGuid
  );
}

export type FarmerActionData = {
  "SubmittedActions.category": string;
  "SubmittedActions.categoryId": string;
  "SubmittedActions.actionId": string;
  "SubmittedActions.action": string;
  "SubmittedActions.submissionDate": string;
  "SubmittedActions.guid": string;
  "SubmittedActions.requiredEvidence": string;
  "SubmittedActions.evidenceCount": number;
  "SubmittedActions.detailQuestion": string;
  "SubmittedActions.submittedDetails": string;
  "SubmittedActions.status": string;
};

export function useFarmerActions(farmerGuid?: string) {
  return useInsightsQuery<FarmerActionData>(
    {
      dimensions: [
        "SubmittedActions.category",
        "SubmittedActions.categoryId",
        "SubmittedActions.actionId",
        "SubmittedActions.action",
        "SubmittedActions.submissionDate",
        "SubmittedActions.guid",
        "SubmittedActions.requiredEvidence",
        "SubmittedActions.evidenceCount",
        "SubmittedActions.detailQuestion",
        "SubmittedActions.submittedDetails",
        "SubmittedActions.status",
      ],
      filters: farmerGuid
        ? [
            {
              member: "SubmittedActions.userGuid",
              operator: "equals",
              values: [farmerGuid],
            },
          ]
        : [],
      order: { "SubmittedActions.category": "asc" },
    },
    `farmerActions_${farmerGuid}`,
    !!farmerGuid
  );
}

// ─── Programme info (for banner) ────────────────────────────

export type ProgrammeInfoData = {
  "HighLevelMetrics.programmeGuid": string;
  "HighLevelMetrics.programmeType": string;
};

export function useProgrammeInfo() {
  return useInsightsQuery<ProgrammeInfoData>(
    {
      dimensions: [
        "HighLevelMetrics.programmeGuid",
        "HighLevelMetrics.programmeType",
      ],
    },
    "programmeInfoHlm"
  );
}

// ─── Milk Quality hooks ─────────────────────────────────────

export type MilkQualityAvgData = {
  "MilkQuality.avgFatPercent": number;
  "MilkQuality.avgProteinPercent": number;
  "MilkQuality.avgSomaticCellCount": number;
  "MilkQuality.avgTotalBacterialCount": number;
  "MilkQuality.avgVolumeLiters": number;
};

const milkQualityAveragesQuery: CubeQuery = {
  measures: [
    "MilkQuality.avgFatPercent",
    "MilkQuality.avgProteinPercent",
    "MilkQuality.avgSomaticCellCount",
    "MilkQuality.avgTotalBacterialCount",
    "MilkQuality.avgVolumeLiters",
  ],
};

export function useMilkQualityAverages() {
  return useInsightsQuery<MilkQualityAvgData>(
    milkQualityAveragesQuery,
    "milkQualityAverages"
  );
}

export type MilkQualityFarmerData = {
  "MilkQuality.fatPercent": number;
  "MilkQuality.proteinPercent": number;
  "MilkQuality.somaticCellCount": number;
  "MilkQuality.totalBacterialCount": number;
  "MilkQuality.volumeLiters": number;
  "MilkQuality.antibioticTest": string;
  "MilkQuality.periodStart": string;
  "MilkQuality.periodEnd": string;
};

export function useMilkQualityFarmer(farmerGuid?: string) {
  return useInsightsQuery<MilkQualityFarmerData>(
    withFarmerFilter(
      {
        dimensions: [
          "MilkQuality.fatPercent",
          "MilkQuality.proteinPercent",
          "MilkQuality.somaticCellCount",
          "MilkQuality.totalBacterialCount",
          "MilkQuality.volumeLiters",
          "MilkQuality.antibioticTest",
          "MilkQuality.periodStart",
          "MilkQuality.periodEnd",
        ],
        order: { "MilkQuality.periodStart": "desc" },
        limit: 1,
      },
      "MilkQuality.userGuid",
      farmerGuid
    ),
    farmerKey("milkQualityFarmer", farmerGuid)
  );
}

// ─── Milk Quality — per-farmer per-period (for distribution charts, alerts, watchlist) ─

export type MilkQualityPerFarmerPeriodData = {
  "MilkQuality.userGuid": string;
  "MilkQuality.periodStart": string;
  "MilkQuality.somaticCellCount": number;
  "MilkQuality.totalBacterialCount": number;
  "MilkQuality.antibioticTest": string;
};

const milkQualityPerFarmerPeriodQuery: CubeQuery = {
  dimensions: [
    "MilkQuality.userGuid",
    "MilkQuality.periodStart",
    "MilkQuality.somaticCellCount",
    "MilkQuality.totalBacterialCount",
    "MilkQuality.antibioticTest",
  ],
  order: { "MilkQuality.periodStart": "asc" },
};

export function useMilkQualityPerFarmerPeriod() {
  return useInsightsQuery<MilkQualityPerFarmerPeriodData>(
    milkQualityPerFarmerPeriodQuery,
    "milkQualityPerFarmerPeriod"
  );
}

// ─── Milk Quality — programme averages over time (trend charts) ─

export type MilkQualityTrendData = {
  "MilkQuality.avgFatPercent": number;
  "MilkQuality.avgProteinPercent": number;
  "MilkQuality.avgVolumeLiters": number;
  "MilkQuality.avgSomaticCellCount": number;
  "MilkQuality.avgTotalBacterialCount": number;
  // Record count per period — multiply by avgVolumeLiters to get total volume.
  "MilkQuality.count": number;
  "MilkQuality.periodStart": string;
};

const milkQualityTrendQuery: CubeQuery = {
  measures: [
    "MilkQuality.avgFatPercent",
    "MilkQuality.avgProteinPercent",
    "MilkQuality.avgVolumeLiters",
    "MilkQuality.avgSomaticCellCount",
    "MilkQuality.avgTotalBacterialCount",
    "MilkQuality.count",
  ],
  dimensions: ["MilkQuality.periodStart"],
  order: { "MilkQuality.periodStart": "asc" },
};

export function useMilkQualityTrend() {
  return useInsightsQuery<MilkQualityTrendData>(
    milkQualityTrendQuery,
    // Bump the key when the query shape changes so stale cached results
    // (without MilkQuality.count) are not reused.
    "milkQualityTrend_v2"
  );
}

// ─── Milk Quality — single farmer history (sparklines) ─

export type MilkQualityHistoryData = {
  "MilkQuality.periodStart": string;
  "MilkQuality.somaticCellCount": number;
  "MilkQuality.totalBacterialCount": number;
  "MilkQuality.fatPercent": number;
  "MilkQuality.proteinPercent": number;
};

export function useMilkQualityHistory(farmerGuid?: string) {
  return useInsightsQuery<MilkQualityHistoryData>(
    withFarmerFilter(
      {
        dimensions: [
          "MilkQuality.periodStart",
          "MilkQuality.somaticCellCount",
          "MilkQuality.totalBacterialCount",
          "MilkQuality.fatPercent",
          "MilkQuality.proteinPercent",
        ],
        order: { "MilkQuality.periodStart": "asc" },
      },
      "MilkQuality.userGuid",
      farmerGuid
    ),
    farmerKey("milkQualityHistory", farmerGuid),
    !!farmerGuid
  );
}
